import { LIBRARY_TOOLBAR_CONTROL_CLASS } from '@/components/library/LibraryToolbar'
import { FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookmarkPlus, Building, Loader2, Lock, Users } from '@digital-twins/design-system/icons'
import {
  DEFAULT_LIBRARY_COLLECTION_SHARING_MODE,
  KnowledgeFacetAggregate,
  LibraryCollectionRules,
  LibraryCollectionSharingMode,
} from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { marketDisplayName } from './libraryHelpers'
import { savedFilterRules } from './saveFilterRules'

export interface SaveFiltersInput {
  name: string
  sharingMode: LibraryCollectionSharingMode
  rules: LibraryCollectionRules
}

/** Whole org first: it is the default (D2). */
const SHARING_OPTIONS: Array<{ mode: LibraryCollectionSharingMode; icon: typeof Lock }> = [
  { mode: 'organization', icon: Building },
  { mode: 'private', icon: Lock },
  { mode: 'shared', icon: Users },
]

const SUGGESTED_NAME_KINDS = ['brand', 'market', 'category', 'method', 'year'] as const

/** A first name from the saved values, in a reading order ("Acme, Mexico, 2026"). */
export function suggestedSavedFilterName(filters: Record<string, string | null>): string {
  return SUGGESTED_NAME_KINDS.map((kind) => {
    const value = filters[kind]
    if (!value || value === '__unknown__') {
      return null
    }
    return kind === 'market' ? marketDisplayName(value) : value
  })
    .filter(Boolean)
    .join(', ')
}

export interface SaveFiltersPopoverProps {
  /** Every active Library filter, the type filter included. */
  filters: Record<string, string | null>
  facetCatalog: Array<KnowledgeFacetAggregate>
  /** A search is typed: it is not saved either. */
  hasSearchQuery: boolean
  onSave: (input: SaveFiltersInput) => Promise<void>
}

/**
 * "Save filters": the Library's rule-able filters become a saved filter, a
 * rules-only collection (D1). Name plus who can see it; disabled until a
 * market, year, method, category or brand filter is set.
 */
export function SaveFiltersPopover(props: SaveFiltersPopoverProps) {
  const { filters, facetCatalog, hasSearchQuery, onSave } = props

  const { t } = useTranslation('app')
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [sharingMode, setSharingMode] = useState<LibraryCollectionSharingMode>(
    DEFAULT_LIBRARY_COLLECTION_SHARING_MODE,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [hasError, setHasError] = useState(false)

  const mapping = useMemo(() => savedFilterRules(filters, facetCatalog), [filters, facetCatalog])
  const hasDropped = mapping.droppedKinds.length > 0 || hasSearchQuery
  const trimmedName = name.trim()

  function onOpenChange(open: boolean) {
    setIsOpen(open)
    if (open) {
      setName(suggestedSavedFilterName(filters))
      setSharingMode(DEFAULT_LIBRARY_COLLECTION_SHARING_MODE)
      setHasError(false)
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!mapping.hasRules || !trimmedName || isSaving) {
      return
    }
    setIsSaving(true)
    setHasError(false)
    try {
      await onSave({ name: trimmedName, sharingMode, rules: mapping.rules })
      setIsOpen(false)
    } catch {
      setHasError(true)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger
        type="button"
        disabled={!mapping.hasRules}
        title={mapping.hasRules ? undefined : t('library.saveFilters.disabledHint')}
        className={cn(LIBRARY_TOOLBAR_CONTROL_CLASS, "inline-flex items-center gap-1.5 border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50")}
        data-testid="knowledge-save-filters"
      >
        <BookmarkPlus className="h-3.5 w-3.5" aria-hidden="true" />
        {t('library.saveFilters.trigger')}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] rounded-xl p-4" data-testid="knowledge-save-filters-popover">
        <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="knowledge-save-filters-name">{t('library.saveFilters.name')}</Label>
            <Input
              id="knowledge-save-filters-name"
              value={name}
              maxLength={120}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              data-testid="knowledge-save-filters-name"
            />
          </div>
          <div
            role="radiogroup"
            aria-labelledby="knowledge-save-filters-sharing-label"
            className="flex flex-col gap-1.5"
          >
            <span id="knowledge-save-filters-sharing-label" className="mb-1 text-sm font-medium text-foreground">
              {t('library.saveFilters.sharing')}
            </span>
            {SHARING_OPTIONS.map((option) => {
              const Icon = option.icon
              const isSelected = sharingMode === option.mode
              return (
                <button
                  key={option.mode}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSharingMode(option.mode)}
                  className={cn(
                    'flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                    isSelected ? 'border-primary/50 bg-primary/5 text-foreground' : 'border-border text-foreground hover:bg-accent',
                  )}
                  data-testid={`knowledge-save-filters-sharing-${option.mode}`}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden="true" />
                  {t(`library.collectionSharing.${option.mode}`)}
                </button>
              )
            })}
          </div>
          {hasDropped ? (
            <p className="text-[12px] text-muted-foreground" data-testid="knowledge-save-filters-dropped">
              {t('library.saveFilters.droppedHint')}
            </p>
          ) : null}
          {hasError ? (
            <p className="text-[12px] text-destructive" role="alert" data-testid="knowledge-save-filters-error">
              {t('library.saveFilters.failed')}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="default"
            size="sm"
            disabled={!trimmedName || isSaving}
            data-testid="knowledge-save-filters-submit"
          >
            {isSaving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
            {t('library.saveFilters.save')}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  )
}
